import React, { useEffect, useState as useReactState } from "react";
import useState from "react-usestateref";
import NeuroCareHeader from "../NeuroCareHeader";
import { db, patientDb } from "../firebase/firebase";
import { ref, child, get } from "firebase/database";
import { Layout, ConfigProvider, Input, Table } from "antd";
import locale from "antd/es/locale/ko_KR";
import { collection, doc, getDoc, getDocs, query, orderBy, limit } from "firebase/firestore";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, LabelList, Cell } from "recharts";
import { useAuth } from "../AuthProvider";
import Loader from "./Loader";
import { SearchOutlined } from "@ant-design/icons";

const Report = () => {
  const { user, authReady } = useAuth();

  const [patientList, setPatientList] = useState([]);
  const [filteredList, setFilteredList] = useState([]);
  const [selectedPatient, setSelectedPatient] = useState(undefined);
  const [patientTests, setPatientTests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [init, setInit] = useState(false);
  const [searchKeyword, setSearchKeyword] = useState("");
  const [extendedActive, setExtendedActive] = useState(false);

  useEffect(() => {
    const checkExtendedWeb = async () => {
      if (!user?.id) return;
      try {
        const infoSnap = await getDoc(doc(db, "Accounts", user.id, "Info", "data"));
        setExtendedActive(
          infoSnap.exists() && infoSnap.data().extendedWeb === true
        );
      } catch (error) {
        console.error("확장검사 활성 여부 확인 실패:", error);
      }
    };
    checkExtendedWeb();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // 병원 전체 환자 명부읽기. 날짜무관.
  const fetchPatients = async () => {
    try {
      setLoading(true);
      const clientsSnap = await get(
        child(ref(patientDb), `Account/${user.id}/Clients`),
      );

      const rows = [];
      if (clientsSnap.exists()) {
        clientsSnap.forEach((c) => {
          const val = c.val() || {};
          const code = String(val.code ?? "").trim();
          const name = String(val.name ?? "").trim();
          if (!code) return;
          rows.push({ _id: c.key, patientId: code, name });
        });
      }
      // 같은 code가 중복 등록돼 있으면 최근 등록분 하나만 남긴다.
      rows.sort((a, b) => a._id.localeCompare(b._id));
      const unique = Array.from(
        new Map(rows.map((item) => [item.patientId, item])).values(),
      );
      // 최근 등록 순으로 표시
      unique.sort((a, b) => b._id.localeCompare(a._id));

      setPatientList(unique);
      setLoading(false);
    } catch (err) {
      console.error("환자 명부 로드 실패:", err);
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!init && user && authReady) {
      setInit(true);
      fetchPatients();
    }
  }, [user, init, authReady]);

  useEffect(() => {
    const keyword = searchKeyword.trim();
    if (!keyword) {
      setFilteredList([]);
      return;
    }
    setFilteredList(
      patientList.filter(
        (item) =>
          (item.name || "").includes(keyword) ||
          (item.patientId || "").includes(keyword),
      ),
    );
  }, [patientList, searchKeyword]);

  // 선택한 환자의 가장 최근 검사 5건 (날짜 제한 없음)
  const fetchPatientTests = async (patientId) => {
    setLoading(true);
    try {
      const testsRef = collection(
        db,
        "Accounts",
        user.id,
        "Patients",
        patientId,
        "Tests",
      );

      const q = query(testsRef, orderBy("createdAt", "desc"), limit(5));
      const snap = await getDocs(q);
      const tests = snap.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
      setPatientTests(tests.reverse());
    } catch (e) {
      console.error("환자 검사 기록 로드 실패:", e);
    }
    setLoading(false);
  };

  useEffect(() => {
    if (selectedPatient) {
      fetchPatientTests(selectedPatient.patientId);
    } else {
      setPatientTests([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPatient]);

  const dateToString = (raw) => {
    if (!raw) return "";
    const month = parseInt(raw.substring(4, 6), 10);
    const day = parseInt(raw.substring(6, 8), 10);
    return `${month}월 ${day}일`;
  };

  const getScoreColor = (val) => {
    if (val === 0) return "#CBD5E1";
    if (val >= 80) return "#3B82F6";
    if (val >= 60) return "#FBBF24";
    return "#F87171";
  };

  const CustomBarShape = (props) => {
    const { fill, x, y, width, height } = props;
    const radius = 8;
    return (
      <g>
        <path
          d={`M${x},${y + height} L${x},${y + radius} Q${x},${y} ${x + radius},${y} L${x + width - radius},${y} Q${x + width},${y} ${x + width},${y + radius} L${x + width},${y + height} Z`}
          fill={fill}
        />
        <circle
          cx={x + width / 2}
          cy={y + 6}
          r={3}
          fill="#ffffff"
          stroke={fill}
          strokeWidth={1.5}
        />
      </g>
    );
  };

  const CustomLabel = (props) => {
    const { x, y, width, value } = props;
    if (x == null || y == null) return null;
    return (
      <text
        x={x + width / 2}
        y={y - 12}
        fill={getScoreColor(value)}
        textAnchor="middle"
        fontSize={14}
        fontWeight="bold"
      >
        {value}
      </text>
    );
  };

  const ScoreChart = ({ title, data }) => (
    <div className="report-chart-box">
      <div className="report-chart-title">{title}</div>
      <div style={{ width: "100%", height: "145px" }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={data}
            margin={{ top: 28, right: 10, bottom: 0, left: 10 }}
          >
            <XAxis
              dataKey="name"
              tick={{ fontSize: 12, fill: "#94A3B8" }}
              axisLine={false}
              tickLine={false}
              dy={5}
            />
            <YAxis domain={[0, 115]} hide />
            <Bar
              dataKey="value"
              barSize={30}
              shape={<CustomBarShape />}
              isAnimationActive={false} /* 애니메이션 끄기 */
            >
              {data.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={getScoreColor(entry.value)} />
              ))}
              <LabelList content={<CustomLabel />} />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );

  const StatusLegend = () => (
    <div className="report-legend">
      <span>
        <div className="legend-dot" style={{ backgroundColor: "#3B82F6" }} />{" "}
        정상 (80+)
      </span>
      <span>
        <div className="legend-dot" style={{ backgroundColor: "#FBBF24" }} />{" "}
        주의 (60~79)
      </span>
      <span>
        <div className="legend-dot" style={{ backgroundColor: "#F87171" }} />{" "}
        위험 (60 미만)
      </span>
    </div>
  );

  const DashboardCharts = () => {
    const faceList = [];
    const pupilList = [];
    const motorList = [];
    const speechList = [];
    const overallList = [];
    const orientationList = [];
    const tongueList = [];
    const cerebellarList = [];
    const gripList = [];

    // 테스트 리포트 값
    patientTests.forEach((test) => {
      const dateTime = test.id || test.createdAt;
      const dateStr = String(dateTime).split("-")[0];
      const date = dateToString(dateStr);
      const total_data = test.total_data || {};

      const face = Number((total_data.face_score || 0).toFixed(1));
      const motor = Number((total_data.motor_score || 0).toFixed(1));
      const pupil = Number((total_data.pupil_score || 0).toFixed(1));
      const speech = Number((total_data.speech_score || 0).toFixed(1));

      faceList.push({ name: date, value: face });
      motorList.push({ name: date, value: motor });
      pupilList.push({ name: date, value: pupil });
      speechList.push({ name: date, value: speech });

      const oData = test.orientation_data;
      const orientationScore =
        oData && oData.total_count > 0
          ? Number(((oData.pass_count / oData.total_count) * 100).toFixed(1))
          : 0;
      const cData = test.cerebellar_data;
      let cerebellarScore = 0;
      if (cData) {
        const parts = [];
        const accs = [cData.left_avg_accuracy, cData.right_avg_accuracy].filter(
          (v) => v != null
        );
        if (accs.length > 0) {
          parts.push({
            weight: 50,
            score: accs.reduce((a, b) => a + b, 0) / accs.length,
          });
        }
        const totalTries =
          (cData.left_total_tries || 0) + (cData.right_total_tries || 0);
        if (totalTries > 0) {
          parts.push({
            weight: 30,
            score:
              (((cData.left_success_count || 0) +
                (cData.right_success_count || 0)) /
                totalTries) *
              100,
          });
        }
        if (cData.left_avg_reaction_ms > 0 && cData.right_avg_reaction_ms > 0) {
          parts.push({
            weight: 20,
            score:
              (Math.min(
                cData.left_avg_reaction_ms,
                cData.right_avg_reaction_ms
              ) /
                Math.max(
                  cData.left_avg_reaction_ms,
                  cData.right_avg_reaction_ms
                )) *
              100,
          });
        }
        const totalWeight = parts.reduce((sum, p) => sum + p.weight, 0);
        if (totalWeight > 0) {
          cerebellarScore = Number(
            (
              parts.reduce((sum, p) => sum + p.score * p.weight, 0) /
              totalWeight
            ).toFixed(1)
          );
        }
      }
      const tongueScore =
        test.tongue_judgment === "center"
          ? 100
          : test.tongue_judgment === "left" || test.tongue_judgment === "right"
            ? 50
            : 0;
      const gData = test.hand_grip_data;
      const gLeft = gData?.left_count;
      const gRight = gData?.right_count;
      const gripScore =
        gLeft != null && gRight != null && Math.max(gLeft, gRight) > 0
          ? Number(
              ((Math.min(gLeft, gRight) / Math.max(gLeft, gRight)) * 100).toFixed(1)
            )
          : 0;

      const average = extendedActive
        ? Number(
            ((face + motor + pupil + speech + orientationScore + tongueScore + cerebellarScore + gripScore) / 8).toFixed(1))
        : Number(((face + motor + pupil + speech) / 4).toFixed(1));
      overallList.push({ name: date, value: average });

      orientationList.push({ name: date, value: orientationScore });
      tongueList.push({ name: date, value: tongueScore });
      cerebellarList.push({ name: date, value: cerebellarScore });
      gripList.push({ name: date, value: gripScore });
    });

    if (patientTests.length === 0) {
      return (
        <div style={{ padding: "40px", color: "#64748b", textAlign: "center" }}>
          최근 검사 데이터가 없습니다.
        </div>
      );
    }

    return (
      <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
        {" "}
        <div className="report-section-card">
          <div className="report-section-title">개별 검사 추이</div>
          <div className="report-grid-2x2">
            <ScoreChart title="얼굴 대칭" data={faceList} />
            <ScoreChart title="동공 운동" data={pupilList} />
            <ScoreChart title="양팔 편측" data={motorList} />
            <ScoreChart title="언어 능력" data={speechList} />
          </div>
          <StatusLegend />
        </div>

        {extendedActive && (
          <div className="report-section-card">
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                marginBottom: "12px",
              }}
            >
              <div
                className="report-section-title"
                style={{ marginBottom: 0 }}
              >
                확장검사 추이
              </div>
              <span
                style={{
                  fontSize: "10px",
                  fontWeight: 700,
                  color: "#16a34a",
                  background: "#eafaf0",
                  padding: "2px 8px",
                  borderRadius: "10px",
                  whiteSpace: "nowrap",
                }}
              >
                인증 병원
              </span>
            </div>
            <div className="report-grid-2x2">
              <ScoreChart title="지남력 검사" data={orientationList} />
              <ScoreChart title="설측검사" data={tongueList} />
              <ScoreChart title="소뇌협응검사" data={cerebellarList} />
              <ScoreChart title="손쥐었다펴기검사" data={gripList} />
            </div>
            <StatusLegend />
          </div>
        )}

        <div className="report-section-card">
          <div className="report-section-title">
            종합 추이(신경학적 검사 추이)
          </div>
          <div className="report-grid-wide">
            <ScoreChart title="" data={overallList} />
          </div>
          <StatusLegend />
        </div>
      </div>
    );
  };

  const handleSearch = (e) => {
    setSearchKeyword(e.target.value);
  };

  const columns = [
    { title: "환자명", dataIndex: "name", key: "name", width: "40%" },
    {
      title: "환자번호",
      dataIndex: "patientId",
      key: "patientId",
      width: "60%",
    },
  ];

  return (
    <Layout style={{ background: "#F8FAFC", minHeight: "100vh" }}>
      <NeuroCareHeader selectedMenu={"분석 리포트"} />
      {loading && <Loader />}
      <ConfigProvider locale={locale}>
        <div style={{ display: "flex", height: "calc(100vh - 60px)" }}>
          <div className="report-sidebar">
            <div style={{ padding: "20px 20px 10px 20px" }}>
              <Input
                placeholder="환자명 또는 환자번호 입력"
                value={searchKeyword}
                onChange={handleSearch}
                prefix={<SearchOutlined style={{ color: "#94A3B8" }} />}
                className="report-search-input"
              />
            </div>
            <div className="report-table-container">
              {searchKeyword.trim() ? (
                <Table
                  className="report-patient-table"
                  columns={columns}
                  dataSource={filteredList}
                  pagination={false}
                  bordered={false}
                  rowKey="patientId"
                  locale={{ emptyText: "일치하는 환자가 없습니다." }}
                  onRow={(record) => {
                    return {
                      onClick: () => setSelectedPatient(record),
                      className:
                        selectedPatient?.patientId === record.patientId
                          ? "report-row-active"
                          : "",
                    };
                  }}
                />
              ) : (
                <div
                  style={{
                    padding: "32px 20px",
                    color: "#94A3B8",
                    fontSize: "13px",
                    textAlign: "center",
                    wordBreak: "keep-all",
                  }}
                >
                  환자명 또는 환자번호를 입력하면
                  <br />
                  일치하는 환자가 표시됩니다.
                </div>
              )}
            </div>
          </div>

          <div className="report-main-content">
            <div className="report-top-bar">
              <div
                style={{
                  display: "flex",
                  gap: "12px",
                  visibility: selectedPatient ? "visible" : "hidden",
                }}
              >
                <div className="report-patient-pill">
                  <span className="pill-label">환자명</span>
                  <span className="pill-value">{selectedPatient?.name}</span>
                </div>
                <div className="report-patient-pill">
                  <span className="pill-label">환자번호</span>
                  <span className="pill-value">
                    {selectedPatient?.patientId}
                  </span>
                </div>
              </div>
            </div>

            {selectedPatient ? (
              <div className="report-scroll-area">
                <DashboardCharts />
              </div>
            ) : (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flex: 1,
                  color: "#94A3B8",
                  fontSize: "16px",
                }}
              >
                좌측 목록에서 환자를 검색하여추가 분석 리포트를 확인하세요.
              </div>
            )}
          </div>
        </div>
      </ConfigProvider>
    </Layout>
  );
};

export default Report;
